/**
 * Protección de los datos en las actualizaciones (design.md, 10.6; Req. 1.10,
 * 2.10).
 *
 * Actualizar el código (Service Worker) no toca IndexedDB. Lo delicado es
 * cuando una versión nueva cambia la estructura de los datos: Dexie migra la
 * base al abrirla con el esquema nuevo. Antes de eso, aquí se abre la base en
 * modo dinámico (sin declarar versión) para leer la versión instalada y, si
 * es menor, guardar una copia interna "antes_de_actualizar".
 *
 * La migración de IndexedDB es atómica: si falla, la base se queda en la
 * versión anterior. La app muestra entonces la pantalla de recuperación, que
 * ofrece descargar la copia o, si no se pudo guardar, los datos tal como
 * están (exportarBaseDinamica).
 */

import Dexie from 'dexie';
import { calcularChecksum } from './checksum';
import { guardarCopia, baseCopias, type BaseCopias } from './copias';
import {
  IDENTIFICADOR_APP,
  FORMATO_VERSION,
  calcularRangoFechas,
  selloLocal,
  serializarRespaldo,
  type ArchivoRespaldo,
} from './backup-format';
import { leerTabla, type MapaTablas } from './types';
import { traducirError } from './tx';
import { ahoraIso } from '@/domain/dates';
import { ErrorDominio } from '@/domain/errors';

/**
 * Versión de Dexie de la base instalada, o null si todavía no existe.
 * Abre y cierra su propia conexión: no deja nada abierto que bloquee la
 * migración.
 */
export async function versionInstalada(nombre: string): Promise<number | null> {
  const dinamica = new Dexie(nombre);
  try {
    await dinamica.open();
    return dinamica.verno;
  } catch (error) {
    // En modo dinámico, Dexie no crea la base si no existe: avisa con este error.
    if (error instanceof Error && error.name === Dexie.errnames.NoSuchDatabase) return null;
    throw error;
  } finally {
    dinamica.close();
  }
}

/**
 * Lee TODAS las tablas de la base instalada, sin declarar esquema, y arma un
 * respaldo. Incluye tablas que esta versión de la app ya no conoce: es la foto
 * completa de lo que hay. Sirve para la copia antes de actualizar y para
 * "Descargar mis datos" en la pantalla de recuperación, cuando la base no abre
 * con el esquema de la app.
 */
export async function exportarBaseDinamica(nombre: string): Promise<ArchivoRespaldo> {
  const dinamica = new Dexie(nombre);
  try {
    await dinamica.open();
    const data: Record<string, unknown[]> = {};
    const counts: Record<string, number> = {};

    await dinamica.transaction('r', dinamica.tables, async () => {
      for (const tabla of dinamica.tables) {
        const registros = await tabla.toArray();
        data[tabla.name] = registros;
        counts[tabla.name] = registros.length;
      }
    });

    const mapa = data as unknown as MapaTablas;
    const ajustes = leerTabla(mapa, 'settings')[0];
    const schemaVersion =
      typeof ajustes?.['schemaVersion'] === 'number' ? ajustes['schemaVersion'] : dinamica.verno;

    return {
      app: IDENTIFICADOR_APP,
      formatVersion: FORMATO_VERSION,
      schemaVersion,
      exportedAt: ahoraIso(),
      device: 'copia-interna',
      perfil: {
        id: typeof ajustes?.['perfilId'] === 'string' ? ajustes['perfilId'] : null,
        nombre: typeof ajustes?.['nombre'] === 'string' ? ajustes['nombre'] : null,
      },
      counts,
      dateRange: calcularRangoFechas(mapa),
      checksum: await calcularChecksum(data),
      data: mapa,
    };
  } finally {
    dinamica.close();
  }
}

/** `"duofit-rescate-2026-10-21-1530.respaldo.json"` */
export function nombreArchivoRescate(fecha = new Date()): string {
  return `duofit-rescate-${selloLocal(fecha)}.respaldo.json`;
}

/**
 * No se pudo guardar la copia antes de actualizar. Lleva los datos ya leídos
 * para que la pantalla de recuperación los ofrezca como archivo aunque la
 * base de copias no tenga espacio.
 */
export class ErrorCopiaAntesDeActualizar extends ErrorDominio {
  readonly respaldo: string;
  readonly nombreArchivo: string;

  constructor(mensaje: string, respaldo: string, nombreArchivo: string) {
    super('copia_antes_de_actualizar', mensaje);
    this.name = 'ErrorCopiaAntesDeActualizar';
    this.respaldo = respaldo;
    this.nombreArchivo = nombreArchivo;
  }
}

export type ResultadoProteccion = 'sin_base' | 'al_dia' | 'copiada';

/**
 * Se llama al arrancar, ANTES de usar la base principal, con la versión que
 * declara el esquema de la app (`db.verno`). Si la base instalada es de una
 * versión anterior, copia todos sus datos a la base de copias y la verifica.
 *
 * Si la copia falla lanza ErrorCopiaAntesDeActualizar: es preferible no abrir
 * la app a migrar sin red de seguridad, y el error lleva los datos para
 * descargarlos desde la pantalla de recuperación.
 */
export async function copiarAntesDeActualizar(
  nombre: string,
  versionNueva: number,
  copias: BaseCopias = baseCopias,
): Promise<ResultadoProteccion> {
  const instalada = await versionInstalada(nombre);
  if (instalada === null) return 'sin_base';
  if (instalada >= versionNueva) return 'al_dia';

  const archivo = await exportarBaseDinamica(nombre);
  try {
    await guardarCopia('antes_de_actualizar', archivo, copias);
  } catch (error) {
    throw new ErrorCopiaAntesDeActualizar(
      'Antes de actualizar tus datos a la versión nueva, la app guarda una copia de ' +
        `seguridad, y esta vez no pudo (${traducirError(error).message}). Tus datos siguen ` +
        'como estaban. Descárgalos con el botón de abajo y libera espacio en el iPhone antes ' +
        'de volver a abrir la app.',
      serializarRespaldo(archivo),
      nombreArchivoRescate(),
    );
  }
  return 'copiada';
}
